import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  BookOpen,
  Filter,
  Library,
  Sparkles,
  FileText,
  Calendar,
  Tag
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { api } from '@/lib/api'
import { formatDate, getStatusColor, getStatusText, buildAuthorizedUrl } from '@/lib/utils'
import type { ApiError } from '@/types'

type SortColumn = 'title' | 'author' | 'status' | 'assetCount' | 'updatedAt'

type SortOrder = 'asc' | 'desc'

const sortLabels: Record<SortColumn, string> = {
  title: '书名',
  author: '作者',
  status: '状态',
  assetCount: '资产数量',
  updatedAt: '更新时间',
}

type PendingDeleteState = {
  mode: 'single' | 'bulk'
  ids: string[]
  titles: string[]
}

export function BooksPage() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [themeFilter, setThemeFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [themes, setThemes] = useState<Array<{
    id: string;
    name: string;
    color?: string;
    level?: number;
    displayName?: string;
    path?: string;
  }>>([])
  const [sortBy, setSortBy] = useState<SortColumn>('updatedAt')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [selectedBooks, setSelectedBooks] = useState<string[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<PendingDeleteState | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const selectAllRef = useRef<HTMLInputElement>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['books', page, statusFilter, themeFilter, searchTerm, sortBy, sortOrder],
    queryFn: () => api.getBooks({
      page,
      size: 20,
      status: statusFilter || undefined,
      theme: themeFilter || undefined,
      search: searchTerm || undefined,
      sortBy,
      sortOrder,
    }),
  })

  useEffect(() => {
    // 获取所有主题（包括没有书籍的）
    const url = buildAuthorizedUrl('/api/themes') || '/api/themes'
    fetch(url)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`加载主题失败（HTTP ${res.status}）`)
        }
        return res.json()
      })
      .then((data) => {
        // 使用扁平化的主题列表
        setThemes(data.themes || [])
      })
      .catch((err) => {
        console.error('Failed to load themes:', err)
      })
  }, [])

  useEffect(() => {
    if (!pendingDelete) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !deleteLoading) {
        setPendingDelete(null)
        setDeleteError(null)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [pendingDelete, deleteLoading])

  const books = useMemo(() => data?.items ?? [], [data?.items])
  const pagination = data?.page

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        selectedBooks.length > 0 && selectedBooks.length < books.length
    }
  }, [selectedBooks, books])

  useEffect(() => {
    setSelectedBooks((prev) => {
      const next = prev.filter((id) => books.some((book) => book.id === id))
      if (next.length === prev.length) {
        let changed = false
        for (let i = 0; i < next.length; i += 1) {
          if (next[i] !== prev[i]) {
            changed = true
            break
          }
        }
        if (!changed) {
          return prev
        }
      }
      return next
    })
  }, [books])

  const handleSearch = () => {
    setPage(1)
    setSearchTerm(searchInput.trim())
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSearch()
    }
  }

  const getBookById = (id: string) => books.find((book) => book.id === id)

  const extractErrorMessage = (error: unknown, fallback: string) => {
    const apiError = error as ApiError | undefined
    return apiError?.message || fallback
  }

  const openDeleteDialog = (ids: string[]) => {
    const uniqueIds = Array.from(new Set(ids))
    const titles = uniqueIds
      .map((bookId) => getBookById(bookId)?.title || '未命名书籍')
      .slice(0, 5)
    setDeleteError(null)
    setPendingDelete({
      mode: uniqueIds.length > 1 ? 'bulk' : 'single',
      ids: uniqueIds,
      titles,
    })
  }

  const handleDeleteBook = (bookId: string) => {
    openDeleteDialog([bookId])
  }

  const handleStatusChange = async (bookId: string, newStatus: string) => {
    try {
      await api.updateBook(bookId, { status: newStatus as 'published' | 'unlisted' | 'archived' })
      refetch()
    } catch (err) {
      console.error('状态更新失败:', err)
      alert(extractErrorMessage(err, '状态更新失败，请重试'))
    }
  }

  const handleCancelDelete = () => {
    if (deleteLoading) return
    setPendingDelete(null)
    setDeleteError(null)
  }

  const confirmDelete = async () => {
    if (!pendingDelete) return
    setDeleteLoading(true)
    setDeleteError(null)

    const failures: Array<{ id: string; message: string }> = []
    const deletedIds: string[] = []
    const currentMode = pendingDelete.mode

    if (currentMode === 'bulk') {
      setBulkLoading(true)
    }

    for (const bookId of pendingDelete.ids) {
      try {
        await api.deleteBook(bookId)
        deletedIds.push(bookId)
      } catch (err) {
        const message = extractErrorMessage(err, '删除失败')
        failures.push({ id: bookId, message })
      }
    }

    if (deletedIds.length > 0) {
      setSelectedBooks((prev) => prev.filter((id) => !deletedIds.includes(id)))
      refetch()
    }

    if (failures.length > 0) {
      const failureMessages = failures
        .map(({ id, message }) => `${getBookById(id)?.title || id}: ${message}`)
        .join('\n')
      setDeleteError(`以下书籍删除失败：\n${failureMessages}`)

      const remainingIds = failures.map((failure) => failure.id)
      const titles = remainingIds
        .map((bookId) => getBookById(bookId)?.title || '未命名书籍')
        .slice(0, 5)

      setPendingDelete({
        mode: remainingIds.length > 1 ? 'bulk' : 'single',
        ids: remainingIds,
        titles,
      })
    } else {
      setPendingDelete(null)
      setDeleteError(null)
    }

    setDeleteLoading(false)
    if (currentMode === 'bulk') {
      setBulkLoading(false)
    }
  }

  const toggleSelectAll = () => {
    if (selectedBooks.length === books.length) {
      setSelectedBooks([])
    } else {
      setSelectedBooks(books.map((book) => book.id))
    }
  }

  const toggleSelectBook = (bookId: string) => {
    setSelectedBooks((prev) =>
      prev.includes(bookId) ? prev.filter((id) => id !== bookId) : [...prev, bookId]
    )
  }

  const handleBulkStatusChange = async (status: 'published' | 'unlisted') => {
    if (selectedBooks.length === 0) return
    setBulkLoading(true)
    const failures: string[] = []

    for (const bookId of selectedBooks) {
      try {
        await api.updateBook(bookId, { status })
      } catch (err) {
        console.error('批量状态更新失败:', err)
        const book = getBookById(bookId)
        const message = extractErrorMessage(err, '状态更新失败')
        failures.push(`${book?.title || bookId}: ${message}`)
      }
    }

    if (failures.length > 0) {
      alert(`以下书籍状态更新失败：\n${failures.join('\n')}`)
    }

    setSelectedBooks([])
    setBulkLoading(false)
    refetch()
  }

  const handleBulkDelete = () => {
    if (selectedBooks.length === 0) return
    openDeleteDialog(selectedBooks)
  }

  const handleSort = (column: SortColumn) => {
    setPage(1)
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortOrder(column === 'updatedAt' ? 'desc' : 'asc')
    }
  }

  const renderSortIcon = (column: SortColumn) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />
    }

    return sortOrder === 'asc' ? (
      <ArrowUp className="h-4 w-4 text-primary-600" />
    ) : (
      <ArrowDown className="h-4 w-4 text-primary-600" />
    )
  }

  const SortButton = ({ column, children }: { column: SortColumn; children: ReactNode }) => (
    <button
      type="button"
      onClick={() => handleSort(column)}
      className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-primary-600"
    >
      {children}
      {renderSortIcon(column)}
    </button>
  )

  const sortSummary = useMemo(() => {
    return `${sortLabels[sortBy]} · ${sortOrder === 'asc' ? '升序' : '降序'}`
  }, [sortBy, sortOrder])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">图书管理</h1>
        </div>
        <div className="py-12 text-center text-gray-500">加载中...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">图书管理</h1>
        </div>
        <div className="space-y-4 rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-red-600">加载失败，请重试</p>
          <Button onClick={() => refetch()} variant="secondary">
            重新加载
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-8">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-black/5" />
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-wider backdrop-blur-sm">
              <BookOpen className="h-4 w-4" /> 图书管理中心
            </div>
            <h1 className="text-3xl font-bold lg:text-4xl">图书管理</h1>
            <p className="max-w-3xl text-sm text-white/90 lg:text-base">
              集中管理后台书籍、资产与发布状态
            </p>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="grid grid-cols-3 gap-3 rounded-2xl bg-white/15 p-4 backdrop-blur-md">
              <div className="text-center">
                <p className="text-2xl font-bold">{pagination?.total || 0}</p>
                <p className="text-xs text-white/80">全部图书</p>
              </div>
              <div className="text-center border-l border-white/20">
                <p className="text-2xl font-bold">{books.filter(b => b.status === 'published').length}</p>
                <p className="text-xs text-white/80">已发布</p>
              </div>
              <div className="text-center border-l border-white/20">
                <p className="text-2xl font-bold">{themes.length}</p>
                <p className="text-xs text-white/80">主题数</p>
              </div>
            </div>
            <Link to="/upload">
              <Button size="lg" className="bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm shadow-xl border border-white/30 font-semibold">
                <Plus className="mr-2 h-5 w-5" />上传新书
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {selectedBooks.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border-2 border-primary-200 bg-gradient-to-r from-primary-50 to-primary-100 px-5 py-4 shadow-lg">
          <span className="text-sm font-semibold text-primary-800">✓ 已选择 {selectedBooks.length} 本书</span>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleBulkStatusChange('published')}
              disabled={bulkLoading}
            >
              批量上架
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleBulkStatusChange('unlisted')}
              disabled={bulkLoading}
            >
              批量下架
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleBulkDelete}
              disabled={bulkLoading}
            >
              批量删除
            </Button>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setSelectedBooks([])} disabled={bulkLoading}>
            清除选择
          </Button>
        </div>
      )}

      <Card className="border-gray-100 shadow-xl">
        <CardHeader className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-gray-100/50">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-600" />
            <CardTitle className="text-lg font-bold">筛选和搜索</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="w-full lg:flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <Input
                  className="pl-10"
                  placeholder="搜索书名、作者或 ISBN..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <select
                value={themeFilter}
                onChange={(e) => {
                  setThemeFilter(e.target.value)
                  setPage(1)
                }}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium shadow-sm hover:border-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
              >
                <option value="">🎯 所有主题</option>
                {themes
                  .sort((a, b) => (a.level || 0) - (b.level || 0) || a.name.localeCompare(b.name))
                  .map((theme) => (
                    <option key={theme.id} value={theme.id}>
                      {'  '.repeat(theme.level || 0) + (theme.displayName || theme.name)}
                    </option>
                  ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setPage(1)
                }}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium shadow-sm hover:border-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
              >
                <option value="">📦 所有状态</option>
                <option value="published">✅ 已发布</option>
                <option value="unlisted">📋 未发布</option>
                <option value="archived">🗄️ 已归档</option>
              </select>
              <Button onClick={handleSearch} className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg">
                <Sparkles className="mr-2 h-4 w-4" />立即搜索
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-gray-100 shadow-xl">
        <CardHeader className="flex flex-col gap-2 border-b border-gray-100 bg-gradient-to-r from-white to-gray-50 md:flex-row md:items-center md:justify-between">
          <CardTitle className="text-lg font-bold text-gray-900">📚 图书列表</CardTitle>
          <p className="text-sm text-gray-600">共 {pagination?.total || 0} 本 · 当前排序：{sortSummary}</p>
        </CardHeader>
        <CardContent className="p-0">
          {books.length === 0 ? (
            <div className="py-20 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 mb-6">
                <Library className="h-10 w-10 text-gray-400" />
              </div>
              <p className="text-lg font-medium text-gray-600 mb-2">暂无图书</p>
              <p className="text-sm text-gray-500 mb-6">开始上传您的第一本电子书</p>
              <Link to="/upload">
                <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-xl">
                  <Plus className="mr-2 h-5 w-5" />上传第一本书
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr className="text-left border-b border-gray-200">
                    <th className="w-12 p-4">
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        checked={selectedBooks.length > 0 && selectedBooks.length === books.length}
                        onChange={toggleSelectAll}
                        aria-label="选择全部"
                      />
                    </th>
                    <th className="p-4 text-xs font-semibold uppercase tracking-wider text-gray-700">🖼️ 封面</th>
                    <th className="p-4 w-80"><SortButton column="title"><BookOpen className="inline h-3 w-3 mr-1" />书名</SortButton></th>
                    <th className="p-4"><SortButton column="author"><Edit className="inline h-3 w-3 mr-1" />作者</SortButton></th>
                    <th className="p-4 text-xs font-semibold uppercase tracking-wider text-gray-700"><Tag className="inline h-3 w-3 mr-1" />主题</th>
                    <th className="p-4"><SortButton column="status">🔄 状态</SortButton></th>
                    <th className="p-4 text-xs font-semibold uppercase tracking-wider text-gray-700">🔐 等级</th>
                    <th className="p-4"><SortButton column="assetCount"><FileText className="inline h-3 w-3 mr-1" />资产</SortButton></th>
                    <th className="p-4"><SortButton column="updatedAt"><Calendar className="inline h-3 w-3 mr-1" />更新时间</SortButton></th>
                    <th className="p-4 text-xs font-semibold uppercase tracking-wider text-gray-700">⚙️ 操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {books.map((book, index) => {
                    const coverImageUrl = buildAuthorizedUrl(book.coverUrl) ?? book.coverUrl ?? undefined;

                    return (
                      <tr
                        key={book.id}
                        className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} transition-all hover:bg-primary-50/50 hover:shadow-md`}
                      >
                        <td className="p-4 align-top">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            checked={selectedBooks.includes(book.id)}
                            onChange={() => toggleSelectBook(book.id)}
                            aria-label={`选择 ${book.title}`}
                          />
                        </td>
                        <td className="p-4 align-top">
                          <div className="h-24 w-16 overflow-hidden rounded-xl border-2 border-gray-200 bg-gradient-to-br from-gray-100 to-gray-200 shadow-lg hover:shadow-xl transition-shadow group">
                            {coverImageUrl ? (
                              <img src={coverImageUrl} alt="封面" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
                            ) : (
                              <div className="flex h-full w-full flex-col items-center justify-center text-gray-400">
                                <BookOpen className="h-6 w-6 mb-1" />
                                <span className="text-[10px]">无封面</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-4 align-top">
                          <div className="space-y-2">
                            <p className="text-sm font-bold text-gray-900 hover:text-blue-600 transition-colors">{book.title}</p>
                            {book.isbn && (
                              <p className="inline-flex items-center gap-1 text-xs text-gray-500">
                                <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">ISBN</span>
                                {book.isbn}
                              </p>
                            )}
                            {book.summary && <p className="line-clamp-2 text-xs text-gray-600 italic">{book.summary}</p>}
                          </div>
                        </td>
                        <td className="p-4 align-top text-sm text-gray-600">
                          {book.author || <span className="text-gray-400">未知</span>}
                        </td>
                        <td className="p-4 align-top">
                          {book.themes && book.themes.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {book.themes.map((theme) => (
                                <span
                                  key={theme.id}
                                className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium group relative"
                                style={{
                                  backgroundColor: theme.color ? `${theme.color}15` : '#f3f4f6',
                                  color: theme.color || '#374151',
                                  borderColor: theme.color || '#e5e7eb',
                                }}
                                title={theme.path || theme.name}
                              >
                                {theme.displayName || theme.name}
                                {(theme.level || 0) > 0 && (
                                  <span className="ml-1 opacity-60">
                                    L{theme.level}
                                  </span>
                                )}
                                {/* Tooltip 显示完整路径 */}
                                {theme.path && theme.path !== `/${theme.id}` && (
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                    {theme.path.split('/').filter(p => p).join(' / ')}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-2 h-2 bg-gray-800 rotate-45"></div>
                                  </div>
                                )}
                              </span>
                            ))}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">无主题</span>
                          )}
                        </td>
                        <td className="p-4 align-top">
                          <span className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-bold shadow-sm ${getStatusColor(book.status)}`}>
                            {book.status === 'published' ? '✅' : book.status === 'unlisted' ? '📋' : '🗄️'} {getStatusText(book.status)}
                          </span>
                        </td>
                        <td className="p-4 align-top">
                          <span
                            className="inline-flex items-center rounded-full bg-purple-50 px-2.5 py-1 text-xs font-semibold text-purple-700 border border-purple-200 shadow-sm"
                            title="数字越小权限越高，默认 100 表示普通用户可访问"
                          >
                            L{book.requiredLevel ?? 0}
                          </span>
                        </td>
                        <td className="p-4 align-top">
                          {book.availableFormats.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {book.availableFormats.map((format) => (
                                <span
                                  key={format}
                                className="inline-flex items-center rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 px-2.5 py-1 text-[11px] font-bold text-blue-700 shadow-sm"
                              >
                                <FileText className="h-3 w-3 mr-1" />
                                {format.toUpperCase()}
                              </span>
                            ))}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">无</span>
                          )}
                          <p className="mt-2 text-xs font-medium text-gray-500">📁 {book.assetCount} 个文件</p>
                        </td>
                        <td className="p-4 align-top">
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-700">{formatDate(book.updatedAt).split(' ')[0]}</p>
                            <p className="text-xs text-gray-500">{formatDate(book.updatedAt).split(' ')[1]}</p>
                          </div>
                        </td>
                        <td className="p-4 align-top">
                          <div className="flex flex-wrap gap-2">
                            <Link to={`/books/${book.id}`}>
                              <Button variant="ghost" size="sm" className="text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-all">
                                <Eye className="h-4 w-4" />
                              </Button>
                          </Link>
                          <Link to={`/books/${book.id}/edit`}>
                            <Button variant="ghost" size="sm" className="text-gray-600 hover:text-green-600 hover:bg-green-50 transition-all">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Link>
                          {book.status === 'published' ? (
                            <Button variant="ghost" size="sm" onClick={() => handleStatusChange(book.id, 'unlisted')}>
                              下架
                            </Button>
                          ) : book.status === 'unlisted' ? (
                            <Button variant="ghost" size="sm" onClick={() => handleStatusChange(book.id, 'published')}>
                              上架
                            </Button>
                          ) : null}
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteBook(book.id)} className="text-gray-600 hover:text-red-600 hover:bg-red-50 transition-all">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex flex-col gap-3 border-t border-gray-200 pt-4 text-sm text-gray-600 md:flex-row md:items-center md:justify-between">
          <p>
            显示第 {((pagination.page - 1) * pagination.size) + 1} - {Math.min(pagination.page * pagination.size, pagination.total)} 项，
            共 {pagination.total} 项
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setPage(page - 1)} disabled={page <= 1}>
              上一页
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= pagination.totalPages}
            >
              下一页
            </Button>
          </div>
        </div>
      )}
      </div>

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={handleCancelDelete}
            aria-hidden
          />
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center gap-3 border-b border-gray-100 px-6 py-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600">
                <Trash2 className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-gray-900">删除书籍</h3>
                <p className="text-sm text-gray-500">
                  {pendingDelete.mode === 'single'
                    ? `确定要删除《${pendingDelete.titles[0]}》吗？此操作不可撤销。`
                    : `确定要删除选中的 ${pendingDelete.ids.length} 本书籍吗？此操作不可撤销。`}
                </p>
              </div>
            </div>
            <div className="max-h-60 overflow-y-auto px-6 py-4 space-y-3">
              {pendingDelete.mode === 'bulk' && (
                <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
                  <p className="font-medium text-gray-700 mb-2">将被删除的书籍：</p>
                  <ul className="space-y-1 text-gray-600">
                    {pendingDelete.titles.map((title, index) => (
                      <li key={`${title}-${index}`} className="flex items-start gap-2">
                        <span className="mt-[2px] h-1.5 w-1.5 rounded-full bg-gray-400" />
                        <span className="flex-1 line-clamp-2">{title}</span>
                      </li>
                    ))}
                  </ul>
                  {pendingDelete.ids.length > pendingDelete.titles.length && (
                    <p className="mt-2 text-xs text-gray-400">
                      还有 {pendingDelete.ids.length - pendingDelete.titles.length} 本书籍未显示。
                    </p>
                  )}
                </div>
              )}

              {deleteError && (
                <div className="whitespace-pre-line rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                  {deleteError}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-6 py-4">
              <Button variant="secondary" onClick={handleCancelDelete} disabled={deleteLoading}>
                稍后再说
              </Button>
              <Button variant="danger" onClick={confirmDelete} loading={deleteLoading}>
                {pendingDelete.mode === 'single'
                  ? '立即删除'
                  : `删除 ${pendingDelete.ids.length} 本`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
